import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface BrandAsset {
  id: string;
  name: string;
  type: 'svg' | 'png' | 'jpg';
  url: string;
  darkMode?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BrandAssetsService {
  private readonly assetsBasePath = '/assets/brand';
  private readonly assets: BrandAsset[] = [
    {
      id: 'logo-light-svg',
      name: 'Logo Light',
      type: 'svg',
      url: `${this.assetsBasePath}/logo-light.svg`,
      darkMode: false
    },
    {
      id: 'logo-dark-svg',
      name: 'Logo Dark',
      type: 'svg',
      url: `${this.assetsBasePath}/logo-dark.svg`,
      darkMode: true
    },
    {
      id: 'logo-light-png',
      name: 'Logo Light',
      type: 'png',
      url: `${this.assetsBasePath}/logo-light.png`,
      darkMode: false
    },
    {
      id: 'logo-dark-png',
      name: 'Logo Dark',
      type: 'png',
      url: `${this.assetsBasePath}/logo-dark.png`,
      darkMode: true
    },
    {
      id: 'logo-light-jpg',
      name: 'Logo Light',
      type: 'jpg',
      url: `${this.assetsBasePath}/logo-light.jpg`,
      darkMode: false
    },
    {
      id: 'logo-dark-jpg',
      name: 'Logo Dark',
      type: 'jpg',
      url: `${this.assetsBasePath}/logo-dark.jpg`,
      darkMode: true
    }
  ];

  constructor(private http: HttpClient) {}

  getAssets(): Observable<BrandAsset[]> {
    return of(this.assets);
  }

  getAssetsByType(type: 'svg' | 'png' | 'jpg'): Observable<BrandAsset[]> {
    return this.getAssets().pipe(
      map(assets => assets.filter(asset => asset.type === type))
    );
  }

  downloadAsset(type: string, darkMode: boolean = false): Observable<Blob> {
    const asset = this.assets.find(a => a.type === type && a.darkMode === darkMode);
    if (!asset) {
      throw new Error(`Asset not found for type: ${type} and darkMode: ${darkMode}`);
    }
    
    return this.http.get(asset.url, { responseType: 'blob' });
  }

  async downloadAndSaveAsset(type: string, darkMode: boolean = false): Promise<void> {
    const asset = this.assets.find(a => a.type === type && a.darkMode === darkMode);
    if (!asset) {
      throw new Error(`Asset not found for type: ${type} and darkMode: ${darkMode}`);
    }

    try {
      const response = await this.http.get(asset.url, { responseType: 'blob' }).toPromise();
      if (!response) {
        throw new Error('Failed to download asset');
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response], { type: `image/${type}` });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `industry-intelligence-logo-${darkMode ? 'dark' : 'light'}.${type}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading asset:', error);
      throw error;
    }
  }
} 